import { useLogout, useUnLinkGoogleAccount } from "@/api/mutations/auth";
import { useGetLinkGoogleAccount, useMe } from "@/api/queries/auth";
import { ReactComponent as GoogleIcon } from "@/assets/icons/google-icon.svg";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ACCESS_TOKEN } from "@/constants/auth";
import { ChangePassword } from "@/pages/profile/components/change-password";
import { TwoFA } from "@/pages/profile/components/two-fa";
import { ExternalLink, FileSpreadsheet, Link2, LogOut, Unlink2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export const Profile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: me, isLoading } = useMe();
  const { mutateAsync: logoutAsync } = useLogout();
  const [googleLinkGetting, setGoogleLinkGetting] = useState(false);
  const { data: googleLinkData } = useGetLinkGoogleAccount(googleLinkGetting);
  const { mutateAsync, isPending: isUnlinking } = useUnLinkGoogleAccount();

  const message = searchParams.get("message");

  useEffect(() => {
    if (googleLinkData) {
      window.location.href = googleLinkData.url;
    }
  }, [googleLinkData]);

  useEffect(() => {
    if (message) {
      switch (searchParams.get("type")) {
        case "success":
          toast.success(message);
          break;
        case "error":
          toast.error(message);
          break;
      }
      setSearchParams(new URLSearchParams({}), { replace: true });
    }
  }, [message]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!me?.data) {
    return null;
  }

  const user = me.data;

  const logout = async () => {
    try {
      await logoutAsync();
      localStorage.removeItem(ACCESS_TOKEN);

      window.location.href = "/login";
      toast.success("Logged out successfully", {
        description: "You have been successfully logged out of your account.",
      });
    } catch {
      toast.error("Logout failed", {
        description: "An error occurred while logging out. Please try again.",
      });
    }
  };

  const handleLinkGoogleAccount = () => {
    setGoogleLinkGetting(true);
  };

  const handleUnLinkGoogleAccount = () => {
    mutateAsync()
      .then(() => {
        toast.success("Google account was successfully unlinked");
      })
      .catch(() => {
        toast.error("Failed to unlink google account");
      });
  };

  const googleSheetUrl = me?.data.googleSheetId
    ? `https://docs.google.com/spreadsheets/d/${me.data.googleSheetId}`
    : undefined;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight dark:text-white">Profile</h2>
        <p className="text-muted-foreground dark:text-white">
          View your account information and manage your profile settings.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">User ID</label>
              <p className="text-lg">{user.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Username</label>
              <p className="text-lg">{user.username}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Role</label>
              <div className="mt-1">
                <Badge variant={user.role === "ADMIN" ? "default" : "secondary"}>{user.role}</Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="mt-1">
                <Badge variant={user.active ? "default" : "destructive"}>
                  {user.active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Google Sheet
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <span data-testid="connection-status">
                {me?.data.googleSheetId ? "Connected" : "Not connected"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-muted-foreground">Spreadsheet ID</span>
              <span className="font-mono break-all text-right">
                {me?.data.googleSheetId ?? "—"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-muted-foreground">Worksheet</span>
              <span>{me?.data.worksheetName ?? "—"}</span>
            </div>
          </div>

          {googleSheetUrl && (
            <a
              href={googleSheetUrl}
              className="flex items-center gap-1 bg-primary w-fit text-white dark:text-black px-4 py-2 rounded-md"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Open Google Sheet
            </a>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <ChangePassword />
            <Button onClick={logout} variant="outline" className="flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Connected Accounts</CardTitle>
          <CardDescription>
            Link your Google account to quickly and securely sign in without entering your password.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg border bg-muted">
                <GoogleIcon className="h-6 w-6" />
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">Google</h3>

                  {user.googleLinkedAt ? (
                    <Badge>Connected</Badge>
                  ) : (
                    <Badge variant="secondary">Not Connected</Badge>
                  )}
                </div>

                <p className="text-sm text-muted-foreground">
                  {user.googleEmail ? user.googleEmail : "No Google account linked"}
                </p>
              </div>
            </div>

            {user.googleLinkedAt ? (
              <AlertDialog>
                <AlertDialogTrigger>
                  <Button data-testid="unlink-btn" variant="destructive" disabled={isUnlinking}>
                    <Unlink2 className="mr-2 h-4 w-4" />
                    Unlink
                  </Button>
                </AlertDialogTrigger>

                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Unlink Google account?</AlertDialogTitle>
                    <AlertDialogDescription>
                      You will no longer be able to sign in with your Google account until it is
                      linked again. You can still sign in using your username and password.
                    </AlertDialogDescription>
                  </AlertDialogHeader>

                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>

                    <AlertDialogAction
                      data-testid="confirm-unlinking-btn"
                      onClick={handleUnLinkGoogleAccount}
                      disabled={isUnlinking}
                    >
                      {isUnlinking ? "Unlinking..." : "Unlink Google"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button disabled={googleLinkGetting} onClick={handleLinkGoogleAccount}>
                <Link2 className="mr-2 h-4 w-4" />
                Link Google
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      <TwoFA />
    </div>
  );
};
